import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFeedInput } from "@/features/study-group-stream/contexts/FeedInputContext";
import {
  INITIAL_REPLIES_VISIBLE,
  INITIAL_VISIBLE,
  resolveReplyParentId,
  shouldPostAsReply,
} from "@/features/study-group-stream/utils/commentSectionUtils";
import { sortTopLevelCommentsDesc } from "@/features/study-group-stream/utils/streamUtil";
import { usePostComments } from "@/hooks/usePostComments";
import type { EditableFileOps } from "@/components/common/hooks/useEditableAttachments";
import type { Comment, ReactionType } from "@/lib/humhub-api/types";

export interface UseCommentSectionProps {
  postContentId: number;
  postObjectId: number;
  studyGroupId?: number | null;
  selectedTopics?: Array<{
    id?: number | string;
    name: string;
    topic_kind?: string;
  }>;
  currentUser?: {
    id: number;
    guid?: string;
    display_name: string;
    url?: string;
    image_url?: string;
  } | null;
  initialComments?: Comment[];
  initialCommentsTotal?: number;
  fetchComments?: boolean;
  initialIsExpanded?: boolean;
  initialExpandedReplyIds?: readonly number[];
  isRetainedRestoration?: boolean;
  mergeInitialCommentsIntoRetainedCache?: boolean;
  onExpansionChange?: (state: {
    isExpanded: boolean;
    expandedReplyIds: number[];
  }) => void;
  onCommentSubmitted?: (
    result: SubmittedCommentResult,
    context: {
      objectId: string | number;
      objectModel?: "comment" | "post";
    },
  ) => void;
  canCreateComments?: boolean;
}

export type SubmittedCommentResult = {
  createdCommentId: number | null;
  parentCommentId: number | null;
};

export function useCommentSection({
  postContentId,
  postObjectId,
  studyGroupId,
  selectedTopics,
  currentUser,
  initialComments,
  initialCommentsTotal,
  fetchComments,
  initialIsExpanded = false,
  initialExpandedReplyIds = [],
  isRetainedRestoration = false,
  mergeInitialCommentsIntoRetainedCache = false,
  onExpansionChange,
  onCommentSubmitted,
  canCreateComments = true,
}: UseCommentSectionProps) {
  const {
    data,
    comments,
    total,
    hasNextPage,
    fetchNextPage,
    isLoading,
    isError,
    isFetchingNextPage,
    isRefetching,
    addComment,
    addReply,
    editComment,
    deleteComment,
    reactComment,
    refetch,
  } = usePostComments({
    postContentId,
    postObjectId,
    groupId: studyGroupId ?? undefined,
    selectedTopics,
    currentUser,
    initialComments,
    initialTotal: initialCommentsTotal,
    enabled: initialComments === undefined || Boolean(fetchComments),
    isRetainedRestoration,
    mergeInitialCommentsIntoRetainedCache,
  });

  // ===== UI State =====
  // コメント入力（返信）のコンテキスト
  const { activeInput, closeAll, toggleReply } = useFeedInput();
  const isReplyForPost =
    activeInput?.type === "reply" && activeInput.postId === postObjectId;
  const replyCtx = isReplyForPost
    ? {
        parentId: activeInput.parentId,
        anchorId: activeInput.anchorId,
        autoFocus: activeInput.autoFocus,
        onDraftRestorationSettled: activeInput.onDraftRestorationSettled,
      }
    : null;
  // 返信が展開されているコメントIDのセット
  const [expandedReplies, setExpandedReplies] = useState<Set<number>>(
    () =>
      new Set(
        initialExpandedReplyIds.filter(
          (id) => Number.isSafeInteger(id) && id > 0,
        ),
      ),
  );
  // トップレベルコメントが展開されているか
  const [isExpanded, setIsExpanded] = useState(initialIsExpanded);
  const [hasRequestedFullComments, setHasRequestedFullComments] =
    useState(false);
  const [isLoadingMoreComments, setIsLoadingMoreComments] = useState(false);
  const restoredReplyFetchSnapshotRef = useRef<string | null>(null);
  const releasedReplyRestorationRef = useRef<string | null>(null);
  const restoredReplyParentId =
    replyCtx?.autoFocus === false ? replyCtx.parentId : null;

  useEffect(() => {
    if (restoredReplyParentId === null) return;
    setIsExpanded(true);
    setExpandedReplies((current) => {
      if (current.has(restoredReplyParentId)) return current;
      const next = new Set(current);
      next.add(restoredReplyParentId);
      return next;
    });
  }, [restoredReplyParentId]);

  // ===== Memoized Derived State =====
  const sortedComments = useMemo(
    () => sortTopLevelCommentsDesc(comments).reverse(),
    [comments],
  );

  const visibleComments = useMemo(
    () =>
      isExpanded ? sortedComments : sortedComments.slice(-INITIAL_VISIBLE),
    [isExpanded, sortedComments],
  );

  const hasMoreComments = isExpanded
    ? !!hasNextPage || comments.length < total
    : total > INITIAL_VISIBLE;

  const restoredReplyTargetLoaded = useMemo(() => {
    if (restoredReplyParentId === null || !replyCtx) return true;
    const parent = sortedComments.find(
      (comment) => Number(comment.id) === restoredReplyParentId,
    );
    if (!parent) return false;
    if (replyCtx.anchorId === restoredReplyParentId) return true;
    const containsAnchor = (items: readonly Comment[]): boolean =>
      items.some(
        (comment) =>
          Number(comment.id) === replyCtx.anchorId ||
          containsAnchor(comment.comments ?? []),
      );
    return containsAnchor(parent.comments ?? []);
  }, [replyCtx, restoredReplyParentId, sortedComments]);

  useEffect(() => {
    onExpansionChange?.({
      isExpanded,
      expandedReplyIds: [...expandedReplies],
    });
  }, [expandedReplies, isExpanded, onExpansionChange]);

  // 各コメントに表示状態を追加
  const commentsWithVisibility = useMemo(() => {
    return visibleComments.map((comment) => {
      const allReplies = sortTopLevelCommentsDesc(
        comment.comments || [],
      ).reverse();
      const commentId = Number(comment.id);
      const areRepliesExpanded = expandedReplies.has(commentId);
      const visibleReplies = areRepliesExpanded
        ? allReplies
        : allReplies.slice(-INITIAL_REPLIES_VISIBLE);
      const hiddenRepliesCount = Math.max(
        0,
        allReplies.length - visibleReplies.length,
      );

      return {
        ...comment,
        visibleReplies,
        hiddenRepliesCount,
        areRepliesExpanded,
      };
    });
  }, [visibleComments, expandedReplies]);

  // ===== Event Handlers =====
  const handleLoadMoreComments = useCallback(async () => {
    setIsLoadingMoreComments(true);
    try {
      if (!isExpanded) {
        setIsExpanded(true);
      }
      if (
        initialComments !== undefined &&
        !fetchComments &&
        !hasRequestedFullComments &&
        comments.length < total
      ) {
        const result = await refetch();
        if (result?.isError) return;
        setHasRequestedFullComments(true);
        return;
      }
      if (hasNextPage) {
        await fetchNextPage();
      }
    } finally {
      setIsLoadingMoreComments(false);
    }
  }, [
    comments.length,
    fetchNextPage,
    fetchComments,
    hasRequestedFullComments,
    hasNextPage,
    initialComments,
    isExpanded,
    refetch,
    total,
  ]);

  useEffect(() => {
    if (
      restoredReplyParentId === null ||
      restoredReplyTargetLoaded ||
      isLoading ||
      isFetchingNextPage ||
      isRefetching
    )
      return;
    const targetKey = `${postObjectId}:${restoredReplyParentId}:${replyCtx?.anchorId}`;
    if (isError) {
      if (releasedReplyRestorationRef.current === targetKey) return;
      releasedReplyRestorationRef.current = targetKey;
      replyCtx?.onDraftRestorationSettled?.();
      return;
    }
    if (
      initialComments !== undefined &&
      !fetchComments &&
      !hasRequestedFullComments
    ) {
      const refetchSnapshot = `refetch:${targetKey}:${comments.length}`;
      if (restoredReplyFetchSnapshotRef.current === refetchSnapshot) return;
      restoredReplyFetchSnapshotRef.current = refetchSnapshot;
      void refetch().then((result) => {
        if (result.isError) {
          restoredReplyFetchSnapshotRef.current = null;
          return;
        }
        setHasRequestedFullComments(true);
      });
      return;
    }
    if (hasNextPage) {
      const fetchSnapshot = `${postObjectId}:${restoredReplyParentId}:${replyCtx?.anchorId}:${data?.pages.length ?? 0}:${comments.length}`;
      if (restoredReplyFetchSnapshotRef.current === fetchSnapshot) return;
      restoredReplyFetchSnapshotRef.current = fetchSnapshot;
      void fetchNextPage().then((result) => {
        if (result.isError) restoredReplyFetchSnapshotRef.current = null;
      });
      return;
    }
    replyCtx?.onDraftRestorationSettled?.();
    closeAll();
  }, [
    closeAll,
    comments.length,
    data?.pages.length,
    fetchNextPage,
    fetchComments,
    hasNextPage,
    hasRequestedFullComments,
    initialComments,
    isError,
    isFetchingNextPage,
    isLoading,
    isRefetching,
    postObjectId,
    refetch,
    replyCtx,
    restoredReplyParentId,
    restoredReplyTargetLoaded,
  ]);

  const handleCollapseComments = useCallback(() => {
    setIsExpanded(false);
  }, []);

  const expandAllComments = useCallback(() => {
    if (!isExpanded) {
      setIsExpanded(true);
    }
  }, [isExpanded]);

  const expandRepliesForComment = useCallback((commentId: number) => {
    setExpandedReplies((prev) => {
      if (prev.has(commentId)) return prev;
      const next = new Set(prev);
      next.add(commentId);
      return next;
    });
  }, []);

  const handleToggleReplies = (commentId: number) => {
    setExpandedReplies((prev) => {
      const next = new Set(prev);
      if (next.has(commentId)) {
        next.delete(commentId);
      } else {
        next.add(commentId);
      }
      return next;
    });
  };

  const handleReplyClick = (parentId: number, anchorId: number) => {
    if (!canCreateComments) return;
    toggleReply(postObjectId, parentId, anchorId);
  };

  const handleClearReplyCtx = useCallback(() => {
    if (isReplyForPost) {
      closeAll();
    }
  }, [isReplyForPost, closeAll]);

  const handleSubmitComment = async (
    content: string,
    objectId: string | number,
    objectModel?: "comment" | "post",
    files?: File[],
  ): Promise<SubmittedCommentResult> => {
    if (!canCreateComments) {
      throw new Error("Comment creation is not permitted");
    }
    if (!shouldPostAsReply(replyCtx, objectModel)) {
      const createdComment = await addComment.mutateAsync({
        content,
        files,
        topics: selectedTopics,
      });
      const result = {
        createdCommentId:
          typeof createdComment.id === "number" ? createdComment.id : null,
        parentCommentId: null,
      };
      onCommentSubmitted?.(result, { objectId, objectModel });
      return result;
    }

    const parentId = resolveReplyParentId(objectId, replyCtx);
    if (!parentId) {
      console.error("Missing parent comment ID for reply submission");
      const result = {
        createdCommentId: null,
        parentCommentId: null,
      };
      onCommentSubmitted?.(result, { objectId, objectModel });
      return result;
    }

    const createdReply = await addReply.mutateAsync({
      parentCommentId: parentId,
      content,
      files,
      topics: selectedTopics,
    });
    expandRepliesForComment(parentId);
    closeAll();
    const result = {
      createdCommentId:
        typeof createdReply.id === "number" ? createdReply.id : null,
      parentCommentId: parentId,
    };
    onCommentSubmitted?.(result, { objectId, objectModel });
    return result;
  };

  const handleDeleteComment = async (commentId: number) => {
    // UIからの確認は呼び出し側で行う
    await deleteComment.mutateAsync({ commentId });
  };

  const handleEditComment = async (
    commentId: number,
    newMessage: string,
    fileOps?: EditableFileOps,
  ) => {
    await editComment.mutateAsync({ commentId, newMessage, fileOps });
  };

  const handleReactComment = async (
    commentId: number,
    reaction: ReactionType | null,
  ) => {
    await reactComment.mutateAsync({ commentId, reaction });
  };

  return {
    // --- State & Data ---
    comments: commentsWithVisibility,
    allComments: sortedComments,
    totalComments: total,
    hasMoreComments,
    isLoadingMoreComments:
      isLoading || isLoadingMoreComments || isFetchingNextPage || isRefetching,
    replyCtx,
    composerAutoFocus: activeInput?.autoFocus !== false,
    composerDraftRestorationSettled:
      activeInput?.type === "postComment"
        ? activeInput.onDraftRestorationSettled
        : undefined,
    currentUser,
    studyGroupId,
    postObjectId,
    isExpanded,

    // --- Mutations & Status ---
    isSubmitting: addComment.isPending || addReply.isPending,
    isEditing: editComment.isPending,
    isDeleting: deleteComment.isPending,
    isReacting: reactComment.isPending,

    // --- Handlers ---
    handleLoadMoreComments,
    handleCollapseComments,
    expandAllComments,
    expandRepliesForComment,
    handleToggleReplies,
    handleReplyClick,
    handleClearReplyCtx,
    handleSubmitComment,
    handleDeleteComment,
    handleEditComment,
    handleReactComment,
  };
}
